import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, collectionGroup } from 'firebase/firestore';
import { db } from '../firebase';

export default function GlobalSearchBar() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    
    // Cache for servers so we don't re-fetch them every time
    const [cachedServers, setCachedServers] = useState([]);
    
    const searchRef = useRef(null);

    // 1. Fetch all Servers for local search
    useEffect(() => {
        const loadServers = async () => {
            try {
                const snap = await getDocs(collection(db, 'servers'));
                const serverList = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    type: 'server'
                }));
                setCachedServers(serverList);
            } catch (err) {
                console.error("Background Server Load Failed:", err);
            }
        };
        loadServers();
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchRef]);

    const performSearch = useCallback(async (rawTerm) => {
        setLoading(true);
        setShowDropdown(true);

        // 1. Search Servers from local cache
        const lowerTerm = rawTerm.toLowerCase();
        
        const matchingServers = cachedServers.filter(server => {
            const name = (server.companyName || "").toLowerCase();
            const id = server.id.toLowerCase();
            return name.includes(lowerTerm) || id.includes(lowerTerm);
        });

        // 2. Search people
        const termOriginal = rawTerm;
        const termTitleCase = rawTerm.charAt(0).toUpperCase() + rawTerm.slice(1).toLowerCase();
        const termUpperCase = rawTerm.toUpperCase(); 
        
        const searchVariations = [...new Set([termOriginal, termTitleCase, termUpperCase])];
        const getServerId = (doc) => doc.ref.parent.parent ? doc.ref.parent.parent.id : null;

        try {
            let promises = [];

            searchVariations.forEach(term => {
                const searchKey = term;
                const endKey = term + '\uf8ff';

                // Employees
                promises.push(
                    getDocs(query(collectionGroup(db, 'employees'), where('fullName', '>=', searchKey), where('fullName', '<=', endKey)))
                    .then(snap => ({ type: 'employee', docs: snap.docs }))
                );

                // Admins
                promises.push(
                    getDocs(query(collectionGroup(db, 'admins'), where('fullName', '>=', searchKey), where('fullName', '<=', endKey)))
                    .then(snap => ({ type: 'admin', docs: snap.docs }))
                );

                // Service Users
                promises.push(
                    getDocs(query(collectionGroup(db, 'serviceUsers'), where('fullName', '>=', searchKey), where('fullName', '<=', endKey)))
                    .then(snap => ({ type: 'serviceUser', docs: snap.docs }))
                );
            });

            // Run DB queries
            const snapshots = await Promise.allSettled(promises);

            // 3. MERGE RESULTS
            const newResults = { 
                servers: matchingServers, 
                employees: [], 
                admins: [], 
                serviceUsers: [] 
            };
            
            const seenIds = new Set();

            snapshots.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    const { type, docs } = result.value;
                    docs.forEach(doc => {
                        if (seenIds.has(doc.id)) return;
                        seenIds.add(doc.id);

                        const data = { id: doc.id, ...doc.data(), type, serverId: getServerId(doc) };
                        
                        if (type === 'employee') newResults.employees.push(data);
                        else if (type === 'admin') newResults.admins.push(data);
                        else if (type === 'serviceUser') newResults.serviceUsers.push(data);
                    });
                } else {
                    console.error(`Search query rejected:`, result.reason);
                }
            });

            setResults(newResults);

        } catch (error) {
            console.error("Search Error:", error);
        } finally {
            setLoading(false);
        }
    }, [cachedServers]);

    // Automatic Search (Debounced)
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchTerm.length >= 2) {
                performSearch(searchTerm);
            } else {
                setResults({});
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, performSearch]);

    const handleNavigate = (item) => {
        setShowDropdown(false);
        setSearchTerm(''); 
        
        if (item.type === 'server') navigate(`/servers/${item.id}`);
        else if (item.serverId) {
            if (item.type === 'employee') navigate(`/servers/${item.serverId}/employees/${item.id}`);
            else if (item.type === 'admin') navigate(`/servers/${item.serverId}/admins/${item.id}`);
            else if (item.type === 'serviceUser') navigate(`/servers/${item.serverId}/service-users/${item.id}`);
        }
    };

    const hasResults = Object.values(results).some(arr => arr && arr.length > 0);

    return (
        <div ref={searchRef} style={{ position: 'relative', width: '400px', maxWidth: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '0 12px' }}>
                <span onClick={() => performSearch(searchTerm)} style={{ fontSize: '18px', cursor: 'pointer', marginRight: '8px', opacity: 0.6 }}>🔍</span>
                <input 
                    type="text" 
                    placeholder="Search Global Database..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => { if(searchTerm.length >= 2) setShowDropdown(true); }}
                    onKeyDown={(e) => e.key === 'Enter' && performSearch(searchTerm)}
                    style={{ border: 'none', outline: 'none', width: '100%', padding: '10px 0', fontSize: '14px', color: '#2d3748' }}
                />
            </div>

            {showDropdown && searchTerm.length >= 2 && (
                <div style={{ 
                    position: 'absolute', top: '110%', left: 0, right: 0, 
                    backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', 
                    zIndex: 9999, maxHeight: '500px', overflowY: 'auto', border: '1px solid #e2e8f0' 
                }}>
                    {loading && <div style={{ padding: '15px', color: '#718096' }}>Searching...</div>}
                    
                    {!loading && !hasResults && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0' }}>No results for "{searchTerm}"</div>
                    )}

                    {!loading && ['servers', 'admins', 'employees', 'serviceUsers'].map(key => (
                        results[key]?.length > 0 && (
                            <div key={key}>
                                <div style={{ padding: '10px 15px', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#f7fafc', color: '#718096', textTransform: 'uppercase', borderBottom: '1px solid #edf2f7' }}>
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                </div>
                                {results[key].map(item => (
                                    <div 
                                        key={item.id} 
                                        onClick={() => handleNavigate(item)}
                                        style={{ padding: '12px 15px', cursor: 'pointer', borderBottom: '1px solid #edf2f7', transition: 'background 0.2s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ebf8ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                    >
                                        <div style={{ fontWeight: '600', color: '#2d3748' }}>{item.companyName || item.fullName}</div>
                                        <div style={{ fontSize: '12px', color: '#718096' }}>{item.username || item.email || item.type}</div>
                                    </div>
                                ))}
                            </div>
                        )
                    ))}
                </div>
            )}
        </div>
    );
}